import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, RefreshCw } from 'lucide-react';
import type { CreatePatientInput, Patient } from '@datazo/contracts';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { usePageTitle } from '@/shared/store/page';
import { useAuthStore } from '@/shared/store/auth';
import { ApiClientError } from '@/shared/api/client';
import { usePatientById, useUpdatePatient } from './api';
import { PatientForm } from './PatientForm';

type Feedback =
  | { kind: 'idle' }
  | { kind: 'error'; message: string }
  | { kind: 'ok'; message: string };

/**
 * Toma un `Patient` (forma del backend, con campos opcionales) y produce
 * los `defaultValues` para el formulario, normalizando `undefined → ''`
 * para que los inputs controlados nunca pierdan el control.
 */
function toFormDefaults(p: Patient): CreatePatientInput {
  return {
    tipoDoc: p.tipoDoc,
    documento: p.documento,
    nombres: p.nombres,
    apellidos: p.apellidos,
    fechaNacimiento: p.fechaNacimiento,
    sexo: p.sexo,
    telefono: p.telefono ?? '',
    direccion: p.direccion ?? '',
    ciudad: p.ciudad ?? 'Barranquilla',
    eps: p.eps ?? '',
    historiaClinica: p.historiaClinica ?? '',
    alergias: p.alergias ?? '',
    contactoEmergencia: p.contactoEmergencia ?? '',
  };
}

export function PatientEditPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: patient, isLoading, isError, error, refetch } = usePatientById(id);
  const updatePatient = useUpdatePatient();
  const [feedback, setFeedback] = useState<Feedback>({ kind: 'idle' });
  // Solo el administrador puede editar la identificación (tipo/número de
  // documento). El resto de roles la ve bloqueada, como hasta ahora.
  const isAdmin = useAuthStore(
    (s) => s.user?.rol === 'admin' || s.user?.esSuperAdmin === true,
  );

  usePageTitle(
    patient !== undefined
      ? `Editar — ${patient.nombres} ${patient.apellidos}`
      : 'Editar paciente',
    'Modificar datos del expediente',
  );

  const defaults = useMemo<CreatePatientInput | null>(
    () => (patient !== undefined ? toFormDefaults(patient) : null),
    [patient],
  );

  const handleSubmit = async (values: CreatePatientInput): Promise<void> => {
    if (id === undefined) return;
    setFeedback({ kind: 'idle' });
    try {
      // El backend acepta PATCH parcial pero enviamos el objeto completo:
      // los campos opcionales vacíos los normalizamos a undefined para no
      // forzar "" como valor persistido.
      const patch: Partial<CreatePatientInput> = {
        tipoDoc: values.tipoDoc,
        documento: values.documento,
        nombres: values.nombres,
        apellidos: values.apellidos,
        fechaNacimiento: values.fechaNacimiento,
        sexo: values.sexo,
      };
      const setIfPresent = (key: 'telefono' | 'direccion' | 'ciudad' | 'eps' | 'historiaClinica' | 'alergias' | 'contactoEmergencia'): void => {
        const v = values[key];
        if (typeof v === 'string' && v.trim() !== '') {
          patch[key] = v;
        }
      };
      setIfPresent('telefono');
      setIfPresent('direccion');
      setIfPresent('ciudad');
      setIfPresent('eps');
      setIfPresent('historiaClinica');
      setIfPresent('alergias');
      setIfPresent('contactoEmergencia');
      const updated = await updatePatient.mutateAsync({ id, patch });
      setFeedback({ kind: 'ok', message: 'Cambios guardados. Redirigiendo…' });
      setTimeout(() => navigate(`/pacientes/${updated.id}`), 600);
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 401 || err.status === 403) {
          setFeedback({ kind: 'error', message: 'No tienes permiso para editar pacientes.' });
          return;
        }
        if (err.status === 409) {
          setFeedback({
            kind: 'error',
            message: 'Conflicto: el documento ya está en uso por otro paciente.',
          });
          return;
        }
        if (err.status === 404) {
          setFeedback({ kind: 'error', message: 'El paciente ya no existe.' });
          return;
        }
        if (err.status >= 400 && err.status < 500) {
          setFeedback({
            kind: 'error',
            message: 'Datos inválidos. Revisa los campos resaltados.',
          });
          return;
        }
      }
      setFeedback({
        kind: 'error',
        message:
          'No fue posible conectar con el servidor. Verifica tu conexión e inténtalo de nuevo.',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-[920px] mx-auto">
        <button
          type="button"
          onClick={() => navigate(`/pacientes/${id ?? ''}`)}
          className="flex items-center gap-2 text-sm text-text-soft hover:text-text mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Volver al expediente
        </button>
        <Card className="p-6" aria-busy="true" aria-live="polite">
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-10 rounded bg-surface-alt animate-pulse" />
            ))}
          </div>
        </Card>
      </div>
    );
  }

  if (isError || defaults === null) {
    return (
      <div className="max-w-[920px] mx-auto">
        <Card>
          <div className="p-8 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-danger-soft text-danger mb-3">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div className="text-text font-bold text-base mb-1">
              No fue posible cargar el paciente
            </div>
            <p className="text-text-muted text-sm mb-4">
              {error?.message ?? 'No encontramos el expediente solicitado.'}
            </p>
            <div className="flex justify-center gap-2">
              {isError && (
                <Button variant="secondary" onClick={() => void refetch()}>
                  <RefreshCw className="w-4 h-4" /> Reintentar
                </Button>
              )}
              <Button variant="secondary" asChild>
                <Link to="/pacientes">
                  <ArrowLeft className="w-4 h-4" /> Volver al listado
                </Link>
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-[920px] mx-auto">
      <button
        type="button"
        onClick={() => navigate(`/pacientes/${id ?? ''}`)}
        className="flex items-center gap-2 text-sm text-text-soft hover:text-text mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Volver al expediente
      </button>

      {isAdmin && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Como administrador puedes editar el tipo y número de documento. Ten en cuenta que el
          número de documento es el usuario y la contraseña del portal del paciente: si lo cambias,
          cambian también sus credenciales de acceso.
        </div>
      )}

      <PatientForm
        defaultValues={defaults}
        submitLabel="Guardar cambios"
        submittingLabel="Guardando…"
        onSubmit={handleSubmit}
        onCancel={() => navigate(`/pacientes/${id ?? ''}`)}
        feedback={feedback}
        lockIdentification={!isAdmin}
      />
    </div>
  );
}
